import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import type { DefaultContext } from '@apollo/client';
import type { DialogId } from 'common/Account/dialogSettingsRegistry';
import { Stack } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import React from 'react';
import { Colors } from 'styles';
import DialogConfirm from './index';
import { useSuppressibleDialogConfirm } from './useSuppressibleDialogConfirm';

type DialogConfirmProps = React.ComponentProps<typeof DialogConfirm>;

type SuppressibleDialogConfirmProps = Omit<
  DialogConfirmProps,
  'footerStart' | 'open' | 'onConfirm'
> & {
  dialogSettingId: DialogId;
  settingsContext: DefaultContext;
  open: boolean;
  onConfirm: DialogConfirmProps['onConfirm'];
  dontShowAgainLabel?: string;
  onSuppressionChange?: (suppressed: boolean) => void | Promise<void>;
  headerText?: string;
  headerVariant?: 'default' | 'warning';
  headerCloseAriaLabel?: string;
  contentTextAlign?: 'left' | 'center';
};

const SuppressibleDialogConfirm = ({
  dialogSettingId,
  settingsContext,
  open,
  onConfirm,
  dontShowAgainLabel = "Don't show again",
  onSuppressionChange,
  headerText,
  headerVariant = 'default',
  headerCloseAriaLabel = 'Close dialog',
  contentTextAlign = 'center',
  slug,
  variant = 'dark',
  dialogWidth = '460px',
  contentWidth: contentWidthProp,
  children,
  onCancel,
  ...props
}: SuppressibleDialogConfirmProps): React.ReactElement => {
  const {
    shouldDisplayDialog,
    isDontShowAgainChecked,
    isUpdatingVisibility,
    handleConfirm,
    handleDontShowAgainChange,
  } = useSuppressibleDialogConfirm({
    dialogSettingId,
    settingsContext,
    open,
    onConfirm,
    onSuppressionChange,
  });

  const showHeader = Boolean(headerText?.trim());
  const contentWidth = contentWidthProp ?? (showHeader ? '100%' : '395px');
  const content = (
    <div
      css={`
        text-align: ${contentTextAlign};
        width: 100%;
      `}
    >
      {children}
    </div>
  );

  return (
    <DialogConfirm
      {...props}
      slug={slug}
      open={shouldDisplayDialog}
      variant={variant}
      dialogWidth={dialogWidth}
      contentWidth={contentWidth}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      footerStart={
        <FormControlLabel
          sx={{ m: 0 }}
          data-testid={`${slug}-suppress-checkbox`}
          control={
            <Checkbox
              size="small"
              checked={isDontShowAgainChecked}
              onChange={handleDontShowAgainChange}
              disabled={isUpdatingVisibility}
            />
          }
          label={
            <Text variant="caption" color={variant === 'light' ? 'secondary-alt' : 'secondary'}>
              {dontShowAgainLabel}
            </Text>
          }
        />
      }
    >
      {showHeader ? (
        <Stack vertical space="small">
          <Stack alignX="between" alignY="center" stretchX>
            <Stack alignY="center" space="small">
              {headerVariant === 'warning' ? (
                <WarningIcon
                  sx={{
                    color: Colors.gray10,
                    fontSize: 24,
                  }}
                />
              ) : null}
              <Text variant="display2">{headerText}</Text>
            </Stack>
            {onCancel != null ? (
              <IconButton
                aria-label={headerCloseAriaLabel}
                data-testid={`${slug}-header-close-button`}
                onClick={onCancel}
                size="large"
                sx={{ color: Colors.gray8, p: '4px' }}
              >
                <CloseOutlinedIcon />
              </IconButton>
            ) : null}
          </Stack>
          {content}
        </Stack>
      ) : (
        content
      )}
    </DialogConfirm>
  );
};

export default SuppressibleDialogConfirm;
